import { useEffect, type MouseEvent } from "react";
import { RedoRounded, UndoRounded } from "@mui/icons-material";
import { Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Box } from "@mui/system";
import { faTrash } from "@fortawesome/pro-light-svg-icons";
import type { GeoJSONStoreFeatures } from "terra-draw";

import { FontAwesomeSvgIcon } from "../../ui/FontAwesomeSvgIcon";
import { useTerraDraw, type TerraDrawMode } from "../hooks";
import { DRAW_TOOLS } from "./drawingTools";

export type MapDrawingControlsProps = {
  /** Whether the drawing toolbar is active. When false the drawing is cleared. */
  enabled?: boolean;
  /** Map id, forwarded to the Terra Draw hook. */
  mapId?: string;
  /** Emitted continuously while the drawn features change. Preview only. */
  onDrawingsChange?: (features: GeoJSONStoreFeatures[]) => void;
  /** Emitted when the user finishes drawing, editing, undoing or clearing. */
  onDrawingsCommit?: (features: GeoJSONStoreFeatures[]) => void;
};

/**
 * MapDrawingControls
 *
 * Toolbar for the Terra Draw powered map drawing. Renders tool toggles
 * (select/circle/polygon/rectangle/freehand) alongside undo/redo/clear actions.
 * All drawing state is owned by `useTerraDraw`; this component is purely the UI.
 */
export function MapDrawingControls({
  enabled,
  mapId,
  onDrawingsChange,
  onDrawingsCommit,
}: MapDrawingControlsProps) {
  const { ready, mode, setMode, undo, redo, clear, canUndo, canRedo, hasFeatures } = useTerraDraw({
    mapId,
    onChange: onDrawingsChange,
    onCommit: onDrawingsCommit,
  });

  // When the tool is switched off, drop any drawings and reset to select mode.
  useEffect(() => {
    if (!enabled) clear();
  }, [enabled, clear]);

  const handleModeChange = (_event: MouseEvent<HTMLElement>, next: TerraDrawMode | null) => {
    // `exclusive` groups emit null when the active button is clicked again;
    // fall back to select so there is always an active mode.
    setMode(next ?? "select");
  };

  if (!enabled || !ready) return null;

  return (
    <Box mt={0.5} sx={{ display: "flex", justifyContent: "center" }}>
      <ToggleButtonGroup
        size="small"
        data-testid="map-drawing-controls"
        sx={{ mr: 0.5, backgroundColor: "white" }}
        onChange={handleModeChange}
        value={mode}
        color="primary"
        exclusive
      >
        {DRAW_TOOLS.map(({ mode: toolMode, testId, icon }) => (
          <ToggleButton
            key={toolMode}
            value={toolMode}
            selected={mode === toolMode}
            size="small"
            data-testid={testId}
          >
            <FontAwesomeSvgIcon icon={icon} />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Button
        onClick={undo}
        disabled={!canUndo}
        size="small"
        variant="contained"
        data-testid="undo"
      >
        <UndoRounded />
      </Button>
      <Button
        onClick={redo}
        disabled={!canRedo}
        size="small"
        variant="contained"
        data-testid="redo"
      >
        <RedoRounded />
      </Button>
      <Button
        size="small"
        onClick={clear}
        color="error"
        variant="contained"
        disabled={!hasFeatures}
        sx={{ ml: 0.5 }}
        data-testid="clear"
      >
        <FontAwesomeSvgIcon icon={faTrash} />
      </Button>
    </Box>
  );
}
